import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Timer, Target, AlertTriangle, ChevronDown, Trophy, Check } from "lucide-react";
import api from "../lib/apiClient";
import { Card, Loading, PrimaryBtn, GhostBtn, Input } from "../components/kit";
import DrillDiagram from "../components/DrillDiagram";

function MarkDone({ date, name, category, skill }) {
  const [count, setCount] = useState(null);
  const mark = async () => {
    try {
      const { data } = await api.post("/drill-complete", { date, drill_name: name, category, skill });
      setCount(data.count);
      toast.success(`Logged · ${data.count}× completed`);
    } catch { toast.error("Could not log completion"); }
  };
  return (
    <button data-testid={`mark-done-${name}`} onClick={mark}
      className={`mt-3 w-full flex items-center justify-center gap-2 border px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
        count != null ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00]"
      }`}>
      <Check size={14} /> {count != null ? `Done · ${count}×` : "Mark done"}
    </button>
  );
}

function DrillCard({ drill, date }) {
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState("");
  const [saved, setSaved] = useState(null);

  const save = async () => {
    if (score === "") return;
    try {
      const { data } = await api.post("/drill-results", {
        date, drill_name: drill.name, skill: drill.skill,
        score: Number(score), unit: drill.score_unit || "points", target: drill.target_score ?? null,
      });
      setSaved(data);
      toast.success(data.improvement != null
        ? `Saved · ${data.improvement >= 0 ? "+" : ""}${data.improvement} vs last time`
        : "Score saved");
    } catch { toast.error("Could not save"); }
  };

  return (
    <Card className="overflow-hidden">
      <button onClick={() => setOpen(!open)} data-testid={`drill-toggle-${drill.name}`}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#1F1F1F] transition-colors">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#CCFF00] mb-1">{drill.skill} · {drill.duration_min} min</div>
          <div className="font-heading text-xl uppercase">{drill.name}</div>
        </div>
        <ChevronDown className={`text-[#A3A3A3] transition-transform ${open ? "rotate-180" : ""}`} size={20} />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-[#262626] pt-4">
          <DrillDiagram type={drill.diagram} />
          <div><span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Purpose</span><p className="text-sm">{drill.purpose}</p></div>
          <div><span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Setup</span><p className="text-sm">{drill.setup}</p></div>
          {drill.equipment && (
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(drill.equipment) ? drill.equipment : [drill.equipment]).map((e, i) =>
                <span key={i} className="text-[10px] uppercase border border-[#262626] px-2 py-1 text-[#A3A3A3]">{e}</span>)}
            </div>
          )}
          {drill.steps && (
            <div>
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Steps</span>
              <ol className="text-sm mt-1 space-y-1 list-decimal list-inside text-[#ddd]">{drill.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-[#262626] p-3"><div className="text-[10px] uppercase text-[#A3A3A3]">Reps / Balls</div><div className="text-sm font-semibold">{drill.balls_reps}</div></div>
            <div className="border border-[#262626] p-3"><div className="text-[10px] uppercase text-[#A3A3A3]">Pass target</div><div className="text-sm font-semibold text-[#CCFF00]">{drill.pass_fail}</div></div>
          </div>
          {drill.scoring && <div><span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Scoring</span><p className="text-sm">{drill.scoring}</p></div>}
          <div className="grid grid-cols-2 gap-3">
            {drill.easier && <div className="border border-[#262626] p-3"><div className="text-[10px] uppercase text-[#A3A3A3]">Easier</div><div className="text-xs">{drill.easier}</div></div>}
            {drill.harder && <div className="border border-[#262626] p-3"><div className="text-[10px] uppercase text-[#A3A3A3]">Harder</div><div className="text-xs">{drill.harder}</div></div>}
          </div>
          {drill.common_mistakes && (
            <div className="border-l-2 border-[#FF3B30] pl-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#FF3B30] flex items-center gap-1"><AlertTriangle size={12} /> Common mistakes</div>
              <ul className="text-xs mt-1 space-y-0.5 text-[#ddd]">{(Array.isArray(drill.common_mistakes) ? drill.common_mistakes : [drill.common_mistakes]).map((m, i) => <li key={i}>• {m}</li>)}</ul>
            </div>
          )}
          {drill.when_to_stop && <p className="text-xs text-[#A3A3A3] italic">When to change: {drill.when_to_stop}</p>}

          {drill.scoreable && (
            <div className="border border-[#CCFF00] p-4 bg-[#CCFF00]/5">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#CCFF00] mb-2 flex items-center gap-1"><Target size={12} /> Log your score ({drill.score_unit || "points"})</div>
              <div className="flex gap-2">
                <Input data-testid={`drill-score-${drill.name}`} type="number" value={score} onChange={(e) => setScore(e.target.value)} placeholder={`Target ${drill.target_score ?? ""}`} />
                <PrimaryBtn data-testid={`drill-save-${drill.name}`} onClick={save}>Save</PrimaryBtn>
              </div>
              {saved && saved.previous_score != null && (
                <div className="text-xs mt-2 text-[#A3A3A3]">Previous: {saved.previous_score} · Now: {saved.score} · <span className={saved.improvement >= 0 ? "text-[#CCFF00]" : "text-[#FF3B30]"}>{saved.improvement >= 0 ? "+" : ""}{saved.improvement}</span></div>
              )}
            </div>
          )}
          <MarkDone date={date} name={drill.name} category="golf" skill={drill.skill} />
        </div>
      )}
    </Card>
  );
}

export default function Training() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [day, setDay] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setDay(null); setErr(false);
    let active = true;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/plan/day/${date}`);
        if (!active) return;
        if (data.status === "ready") { setDay(data.day); return; }
        setDay((prev) => prev || data.day); // show skeleton header while generating
        timer = setTimeout(poll, 3500);
      } catch { if (active) setErr(true); }
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [date]);

  if (err) return <div className="p-8"><Card className="p-8 text-center"><p className="text-[#A3A3A3] mb-4">Could not load this day.</p><GhostBtn onClick={() => navigate("/calendar")}>Back to calendar</GhostBtn></Card></div>;
  if (!day) return <div className="p-6"><Loading text="BUILDING TODAY'S SESSION…" /></div>;

  const generating = !day.drills;
  if (generating) {
    return (
      <div className="p-5 sm:p-8 max-w-3xl mx-auto">
        <button onClick={() => navigate("/calendar")} className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white mb-4"><ArrowLeft size={16} /> Calendar</button>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#CCFF00] mb-1">{day.label} · {day.phase}</div>
        <h1 className="font-heading text-3xl sm:text-4xl uppercase mb-6">{day.is_tournament_day ? "Tournament Day" : day.title}</h1>
        <Card className="p-8 text-center">
          <div className="font-heading text-2xl text-[#CCFF00] animate-pulse mb-3">DESIGNING YOUR SESSION…</div>
          <p className="text-sm text-[#A3A3A3]">The coach is building today's drills, mobility & strength based on your priorities. This takes ~30–60 seconds.</p>
        </Card>
      </div>
    );
  }

  const dr = day.drills || {};
  const session = dr.session || day.session || {};
  const total = (session.golf || 0) + (session.mobility || 0) + (session.strength || 0);

  return (
    <div className="p-5 sm:p-8 max-w-3xl mx-auto">
      <button onClick={() => navigate("/calendar")} className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white mb-4" data-testid="training-back">
        <ArrowLeft size={16} /> Calendar
      </button>
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#CCFF00] mb-1">{day.label} · {day.phase}</div>
      <h1 className="font-heading text-3xl sm:text-4xl uppercase mb-2">{day.is_tournament_day ? "Tournament Day" : day.title}</h1>

      {dr.is_tournament_day ? (
        <div className="space-y-4 mt-6">
          <Card className="p-6 border-l-2 border-[#FF3B30]">
            <div className="flex items-center gap-2 text-[#FF3B30] font-heading text-2xl uppercase mb-2"><Trophy size={22} /> Compete</div>
            <p className="text-sm">{dr.objective}</p>
          </Card>
          {[["Morning", dr.morning], ["Mental", dr.mental]].map(([t, list]) => list && (
            <Card key={t} className="p-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00] mb-3">{t}</div>
              <ul className="text-sm space-y-1.5">{list.map((x, i) => <li key={i}>• {x}</li>)}</ul>
            </Card>
          ))}
          {[["10-Minute Activation", dr.activation], ["Range Warm-up", dr.range_warmup], ["Putting Warm-up", dr.putting_warmup]].map(([t, list]) => list && (
            <Card key={t} className="p-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00] mb-3">{t}</div>
              <div className="space-y-2">{list.map((x, i) => (
                <div key={i} className="flex items-center justify-between border border-[#262626] px-3 py-2">
                  <span className="text-sm font-semibold">{x.name}</span><span className="text-sm text-[#CCFF00]">{x.detail}</span>
                </div>))}</div>
            </Card>
          ))}
          {dr.safety_note && <Card className="p-4 border-l-2 border-[#FF3B30]"><p className="text-xs text-[#A3A3A3]"><AlertTriangle size={12} className="inline mr-1 text-[#FF3B30]" />{dr.safety_note}</p></Card>}
        </div>
      ) : (
        <>
          {/* Objective + session */}
          <Card className="p-6 mt-4">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-1">Today's Objective</div>
            <p className="text-lg font-semibold mb-5">{dr.objective || day.objective}</p>
            <div className="flex items-baseline gap-2 mb-4"><Timer size={16} className="text-[#CCFF00]" /><span className="font-heading text-4xl text-[#CCFF00]">{total}</span><span className="text-xs uppercase text-[#A3A3A3]">min total</span></div>
            <div className="grid grid-cols-3 gap-3">
              {[["Golf", session.golf], ["Mobility", session.mobility], ["Strength", session.strength]].map(([k, v]) => (
                <div key={k} className="border border-[#262626] p-3 text-center"><div className="font-heading text-2xl">{v || 0}</div><div className="text-[10px] uppercase text-[#A3A3A3]">{k} min</div></div>
              ))}
            </div>
          </Card>

          {dr.golf_drills?.length > 0 && (
            <section className="mt-8">
              <h2 className="font-heading text-2xl uppercase mb-4">Golf Drills</h2>
              <div className="space-y-3">{dr.golf_drills.map((d, i) => <DrillCard key={i} drill={d} date={date} />)}</div>
            </section>
          )}

          {dr.mobility?.length > 0 && (
            <section className="mt-8">
              <h2 className="font-heading text-2xl uppercase mb-4">Mobility</h2>
              <div className="grid sm:grid-cols-2 gap-3">{dr.mobility.map((m, i) => (
                <Card key={i} className="p-4">
                  <DrillDiagram type={m.diagram || "mobility"} />
                  <div className="font-heading text-lg uppercase mt-3">{m.name}</div>
                  <div className="text-sm text-[#CCFF00]">{m.sets_reps}</div>
                  <p className="text-xs text-[#A3A3A3] mt-1">{m.golf_relevance || m.purpose}</p>
                  <MarkDone date={date} name={m.name} category="mobility" skill="Mobility" />
                </Card>
              ))}</div>
            </section>
          )}

          {dr.strength?.length > 0 && (
            <section className="mt-8">
              <h2 className="font-heading text-2xl uppercase mb-4">Strength</h2>
              <div className="grid sm:grid-cols-2 gap-3">{dr.strength.map((m, i) => (
                <Card key={i} className="p-4">
                  <DrillDiagram type={m.diagram || "strength"} />
                  <div className="font-heading text-lg uppercase mt-3">{m.name}</div>
                  <div className="text-sm text-[#CCFF00]">{m.sets_reps}</div>
                  {m.equipment && <div className="text-[10px] uppercase text-[#A3A3A3] mt-1">{m.equipment}</div>}
                  <p className="text-xs text-[#A3A3A3] mt-1">{m.golf_relevance || m.purpose}</p>
                  <MarkDone date={date} name={m.name} category="strength" skill="Strength" />
                </Card>
              ))}</div>
            </section>
          )}

          {(dr.recovery || dr.safety_note) && (
            <Card className="p-4 mt-8 border-l-2 border-[#CCFF00]">
              {dr.recovery && <p className="text-sm text-[#A3A3A3] mb-1">Recovery: {dr.recovery}</p>}
              {dr.safety_note && <p className="text-xs text-[#A3A3A3]"><AlertTriangle size={12} className="inline mr-1 text-[#FF3B30]" />{dr.safety_note}</p>}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
